import { z } from 'zod';
import { Condition, questionRefs, questionTests } from './conditions.js';
import { ControlDefinition, INDUSTRIES } from './controls.js';
import { DocumentTypeDefinition } from './documents.js';
import { formAnswerTests, formReferences, InsurerFormDefinition } from './insurer-forms.js';
import { IndustryProfileDefinition } from './profiles.js';
import { QuestionDefinition } from './questions.js';
import { Route } from './routes.js';
import { ORDERED_RULE_OPERATORS, RuleDefinition, ruleQuestionTests } from './rules.js';
import { WorkflowDefinition, workflowRefs } from './workflows.js';
/**
 * Configuration items: everything the studio edits. Drafts are validated one by one on save (shape) and as
 * a whole set before approval (references, cycles, option values used by conditions).
 */
export const CONFIG_KINDS = [
    'journey',
    'stage',
    'question',
    'control',
    'workflow',
    'rule',
    'document_type',
    'industry_profile',
    'insurer_form',
];
export const ConfigKind = z.enum(CONFIG_KINDS);
/** Inheritance levels, most general first. The platform tenant authors the first three. */
export const SCOPE_LEVELS = ['platform', 'country', 'industry', 'tenant', 'product', 'customer'];
export const ScopeLevel = z.enum(SCOPE_LEVELS);
export const PLATFORM_LEVELS = new Set(['platform', 'country', 'industry']);
export const ItemKey = z
    .string()
    .regex(/^[A-Za-z0-9][A-Za-z0-9_.-]{0,63}$/, 'letters, digits, _ . - (max 64)');
export const StageDefinition = z.strictObject({
    name: z.string().trim().min(1).max(200),
    description: z.string().max(1000).default(''),
    /**
     * How the stage reads in the journey builder. Runtime behaviour comes from the journey's prerequisites
     * (stages with the same prerequisites run in parallel) and the entry condition; a conditional stage must
     * have one.
     */
    mode: z.enum(['sequential', 'parallel', 'conditional']).default('sequential'),
    /** Entered only when this holds; decided once the stage's prerequisites are met, otherwise skipped. */
    entry: Condition.optional(),
    /** A mandatory stage must be completed (or skipped) for the journey to complete. */
    mandatory: z.boolean().default(true),
    /** In the order they are asked. */
    questions: z.array(z.string().min(1)).default([]),
    /**
     * `configured`: asked in the order listed. `ranked`: the next-best-question ranking orders them
     * (mandatory ones still first), so the most valuable question comes next (decision 0022).
     */
    order: z.enum(['configured', 'ranked']).default('configured'),
    /**
     * Branches (task 85): after one of this stage's questions is answered, skip ahead to a later question,
     * past named questions, the rest of the stage, later stages or the rest of the journey. Tried in order.
     */
    routes: z.array(Route).max(100).default([]),
    /**
     * Completed when every mandatory question still shown is answered and, if set, this also holds (for
     * example "the kill-switch questions are answered").
     */
    completion: Condition.optional(),
    workflows: z.array(z.string().min(1)).default([]),
    estimated_minutes: z.number().int().positive().optional(),
    completion_message: z.string().max(500).default(''),
});
/**
 * A stage waits for each prerequisite to be completed (or skipped), or only started when `when` is
 * "started" (the prototype's "S05 unlocks once S04 is started"). A bare key means completed.
 */
export const Prerequisite = z
    .union([
    z.string().min(1),
    z.strictObject({ stage: z.string().min(1), when: z.enum(['completed', 'started']).default('completed') }),
])
    .transform((p) => (typeof p === 'string' ? { stage: p, when: 'completed' } : p));
export const JourneyDefinition = z.strictObject({
    name: z.string().trim().min(1).max(200),
    description: z.string().max(1000).default(''),
    /**
     * How much of what applies must be answered for a reliable score (task 86), as a share of the
     * applicable controls. Past it, remaining optional questions are offered, not pressed. Default 0.8.
     */
    coverage: z.number().min(0.3).max(1).optional(),
    /** Listed in display order; a stage's prerequisites must be listed in the same journey. */
    stages: z
        .array(z.strictObject({ stage: z.string().min(1), after: z.array(Prerequisite).default([]) }))
        .min(1),
});
export const BODY_SCHEMAS = {
    journey: JourneyDefinition,
    stage: StageDefinition,
    question: QuestionDefinition,
    control: ControlDefinition,
    workflow: WorkflowDefinition,
    rule: RuleDefinition,
    document_type: DocumentTypeDefinition,
    industry_profile: IndustryProfileDefinition,
    insurer_form: InsurerFormDefinition,
};
export function parseBody(kind, body) {
    return BODY_SCHEMAS[kind].safeParse(body);
}
/** A short human label for lists: question text, or the name of anything else. */
export function itemTitle(kind, body) {
    return String((kind === 'question' ? body.text : body.name) ?? '');
}
/**
 * Validates a whole draft set: every body's shape, then the links between items. References resolve by
 * key within a kind; an override at a narrower scope shares its key with what it overrides, so a key
 * appearing at several scopes is fine as long as each body is valid.
 */
export function validateConfigSet(items) {
    const issues = [];
    const add = (it, path, code, message) => issues.push({ itemId: it.id, kind: it.kind, key: it.key, path, code, message });
    // Every scope's variant of a key is kept: each one is checked, and a reference to a key is satisfied
    // by any of them (which one applies depends on the assessment's context).
    const parsed = new Map(CONFIG_KINDS.map((k) => [k, new Map()]));
    for (const it of items) {
        const r = parseBody(it.kind, it.body);
        if (!r.success) {
            r.error.issues.forEach((i) => add(it, i.path.join('.'), 'invalid_shape', i.message));
            continue;
        }
        const byKey = parsed.get(it.kind);
        byKey.set(it.key, [...(byKey.get(it.key) ?? []), { item: it, body: r.data }]);
    }
    const all = (kind) => [...parsed.get(kind).values()].flat();
    const questions = parsed.get('question');
    const stages = parsed.get('stage');
    // Questions: conditions and dependencies point at real questions, with real option values, and no cycles.
    for (const { item, body } of all('question')) {
        const q = body;
        questionRefs(q.visibility).forEach((ref) => {
            if (!questions.has(ref))
                add(item, 'visibility', 'unknown_reference', `visibility reads unknown question ${ref}`);
        });
        q.depends_on.forEach((ref, i) => {
            if (!questions.has(ref))
                add(item, `depends_on.${i}`, 'unknown_reference', `depends on unknown question ${ref}`);
        });
        checkOptionValues(item, 'visibility', q.visibility, questions, add);
        // Control references are checked once a library is part of the set (the platform publishes it).
        if (parsed.get('control').size)
            q.metadata.controls.forEach((ref, i) => {
                if (!parsed.get('control').has(ref))
                    add(item, `metadata.controls.${i}`, 'unknown_reference', `maps to unknown control ${ref}`);
            });
    }
    // A route makes each question it skips read the question it follows and its condition (task 85).
    const routeReads = new Map();
    for (const { body } of [...parsed.get('stage').values()].flat()) {
        const st = body;
        st.routes.forEach((r) => {
            const reads = [r.after, ...questionRefs(r.when)];
            // Reading a question it skips is reported as route_order, not again as a loop.
            for (const t of stageLocalTargets(st, r))
                routeReads.set(t, [...(routeReads.get(t) ?? []), ...reads.filter((x) => x !== t)]);
        });
    }
    // A loop through any combination of variants is reported (conservative: it could apply in some context).
    for (const cycle of findCycles([...questions.values()], [...questions.keys()], (vs) => vs.flatMap(({ item, body }) => {
        const q = body;
        return [...questionRefs(q.visibility), ...q.depends_on, ...(routeReads.get(item.key) ?? [])];
    }))) {
        const first = questions.get(cycle[0])[0].item;
        add(first, 'visibility', 'cycle', `questions depend on each other in a loop: ${[...cycle, cycle[0]].join(' → ')}`);
    }
    for (const { item, body } of all('stage')) {
        const st = body;
        st.questions.forEach((ref, i) => {
            if (!questions.has(ref))
                add(item, `questions.${i}`, 'unknown_reference', `stage lists unknown question ${ref}`);
        });
        st.workflows.forEach((ref, i) => {
            if (!parsed.get('workflow').has(ref))
                add(item, `workflows.${i}`, 'unknown_reference', `stage starts unknown workflow ${ref}`);
        });
        for (const [path, c] of [
            ['entry', st.entry],
            ['completion', st.completion],
        ]) {
            questionRefs(c).forEach((ref) => {
                if (!questions.has(ref))
                    add(item, path, 'unknown_reference', `${path} condition reads unknown question ${ref}`);
            });
            checkOptionValues(item, path, c, questions, add);
        }
        if (st.mode === 'conditional' && !st.entry)
            add(item, 'entry', 'missing_condition', 'a conditional stage needs an entry condition');
        checkStageRoutes(item, st, questions, stages, add);
    }
    // Document types: checked once a set has any (the platform publishes the starter types).
    const controls = parsed.get('control');
    const docTypes = parsed.get('document_type');
    if (docTypes.size)
        for (const { item, body } of all('question')) {
            const settings = body.settings;
            (settings.document_types ?? []).forEach((ref, i) => {
                if (!docTypes.has(ref))
                    add(item, `settings.document_types.${i}`, 'unknown_reference', `asks for unknown document type ${ref}`);
            });
            // Claim checks (task 62) read document types, and a field check a field of its type.
            (body.checks ?? []).forEach((c, i) => {
                const types = c.kind === 'document_field' ? [c.document_type] : c.document_types;
                for (const ref of types)
                    if (!docTypes.has(ref))
                        add(item, `checks.${i}`, 'unknown_reference', `checks against unknown document type ${ref}`);
                if (c.kind !== 'document_field')
                    return;
                const type = docTypes.get(c.document_type)?.[0]?.body;
                if (type && !type.fields.some((f) => f.key === c.field))
                    add(item, `checks.${i}.field`, 'unknown_reference', `${c.document_type} has no field ${c.field}`);
                if (type && c.rule === 'not_expired' && type.fields.find((f) => f.key === c.field)?.type !== 'date')
                    add(item, `checks.${i}.rule`, 'type_mismatch', `${c.field} isn’t a date, so it can’t expire`);
            });
        }
    if (controls.size)
        for (const { item, body } of all('document_type'))
            body.controls.forEach((ref, i) => {
                if (!controls.has(ref))
                    add(item, `controls.${i}`, 'unknown_reference', `evidences unknown control ${ref}`);
            });
    // Rules: they read real questions with real option values, and their effects point at real things.
    for (const { item, body } of all('rule')) {
        const r = body;
        const tests = ruleQuestionTests(r.when);
        for (const ref of new Set(tests.map((t) => t.question)))
            if (!questions.has(ref))
                add(item, 'when', 'unknown_reference', `rule reads unknown question ${ref}`);
        checkTestValues(item, 'when', tests, questions, add);
        r.then.forEach((e, i) => {
            const p = e.params;
            const refs = [
                ['question', p.question, 'asks'],
                ...(Array.isArray(p.questions) ? p.questions : []).map((q) => ['question', q, 'skips']),
                ['workflow', p.workflow, e.type === 'complete_workflow' ? 'completes' : 'starts'],
                // Control references are checked once a library is part of the set, as for questions.
                ...(controls.size ? [['control', p.control, 'names']] : []),
                ...(docTypes.size
                    ? [['document_type', p.document_type, 'asks for']]
                    : []),
            ];
            for (const [kind, ref, verb] of refs)
                if (typeof ref === 'string' && !parsed.get(kind).has(ref))
                    add(item, `then.${i}.params`, 'unknown_reference', `rule ${verb} unknown ${kind} ${ref}`);
        });
    }
    // Insurer forms (task 88): every control and question a field reads exists, with the options it compares.
    for (const { item, body } of all('insurer_form')) {
        const form = body;
        const refs = formReferences(form);
        if (parsed.get('control').size)
            for (const c of refs.controls)
                if (!parsed.get('control').has(c))
                    add(item, 'sections', 'unknown_reference', `form reads unknown control ${c}`); // prettier-ignore
        for (const q of refs.questions)
            if (!questions.has(q))
                add(item, 'sections', 'unknown_reference', `form reads unknown question ${q}`);
        checkTestValues(item, 'sections', formAnswerTests(form), questions, add);
    }
    // Industry profiles (task 79): one per industry, naming a real journey and real controls.
    for (const { item, body } of all('industry_profile')) {
        const p = body;
        if (!(item.key in INDUSTRIES))
            add(item, 'key', 'unknown_reference', `${item.key} isn’t an industry (${Object.keys(INDUSTRIES).join(', ')})`); // prettier-ignore
        if (p.journey && parsed.get('journey').size && !parsed.get('journey').has(p.journey))
            add(item, 'journey', 'unknown_reference', `profile follows unknown journey ${p.journey}`);
        const seen = new Set();
        p.kill_switches.forEach((k, i) => {
            if (seen.has(k.control))
                add(item, `kill_switches.${i}.control`, 'duplicate_key', `${k.control} is listed twice`);
            seen.add(k.control);
            if (controls.size && !controls.has(k.control))
                add(item, `kill_switches.${i}.control`, 'unknown_reference', `kill switch on unknown control ${k.control}`); // prettier-ignore
        });
    }
    for (const { item, body } of all('journey')) {
        const j = body;
        const inJourney = new Set();
        j.stages.forEach((s, i) => {
            if (inJourney.has(s.stage))
                add(item, `stages.${i}.stage`, 'duplicate_key', `stage ${s.stage} appears twice`);
            inJourney.add(s.stage);
            if (!stages.has(s.stage))
                add(item, `stages.${i}.stage`, 'unknown_reference', `journey uses unknown stage ${s.stage}`);
        });
        j.stages.forEach((s, i) => s.after.forEach((a, k) => {
            if (!inJourney.has(a.stage))
                add(item, `stages.${i}.after.${k}`, 'unknown_reference', `stage ${s.stage} waits for ${a.stage}, which is not in this journey`);
        }));
        // A stage listed twice still has one set of prerequisites: merge them before looking for loops.
        const waitsFor = new Map();
        j.stages.forEach((s) => waitsFor.set(s.stage, [...(waitsFor.get(s.stage) ?? []), ...s.after.map((a) => a.stage)]));
        const cycles = findCycles([...waitsFor.values()], [...waitsFor.keys()], (after) => after);
        for (const cycle of cycles) {
            add(item, 'stages', 'cycle', `stages wait for each other in a loop: ${[...cycle, cycle[0]].join(' → ')}`);
        }
        if (!cycles.length)
            checkStageOrder(item, j, waitsFor, stages, questions, add);
    }
    // Workflows: the questions, document types and sub-workflows they use exist, and no workflow runs
    // itself through its sub-workflows.
    const workflows = parsed.get('workflow');
    for (const { item, body } of all('workflow')) {
        const refs = workflowRefs(body);
        for (const q of new Set(refs.questions))
            if (!questions.has(q))
                add(item, 'nodes', 'unknown_reference', `workflow asks unknown question ${q}`);
        if (docTypes.size)
            for (const d of new Set(refs.document_types))
                if (!docTypes.has(d))
                    add(item, 'nodes', 'unknown_reference', `workflow asks for unknown document type ${d}`);
        for (const w of new Set(refs.workflows))
            if (!workflows.has(w))
                add(item, 'nodes', 'unknown_reference', `workflow runs unknown workflow ${w}`);
    }
    for (const cycle of findCycles([...workflows.values()], [...workflows.keys()], (vs) => vs.flatMap(({ body }) => workflowRefs(body).workflows))) {
        add(workflows.get(cycle[0])[0].item, 'nodes', 'cycle', `workflows run each other in a loop: ${[...cycle, cycle[0]].join(' → ')}`); // prettier-ignore
    }
    return issues;
}
/**
 * Catches journeys that could stall: a question can only wait for (or be shown depending on) a question
 * asked in its own stage or one that comes before it, and a stage's entry condition can only read questions from stages before it (it is
 * decided before the stage opens). Each question belongs to at most one stage of a journey.
 */
function checkStageOrder(item, j, waitsFor, stages, questions, add) {
    const before = new Map();
    const ancestors = (key) => {
        let out = before.get(key);
        if (out)
            return out;
        out = new Set();
        before.set(key, out); // no cycles here: checked before calling
        for (const p of waitsFor.get(key) ?? []) {
            out.add(p);
            ancestors(p).forEach((a) => out.add(a));
        }
        return out;
    };
    const stageOf = new Map();
    const bodies = (key) => (stages.get(key) ?? []).map((v) => v.body);
    j.stages.forEach((s, i) => {
        for (const q of new Set(bodies(s.stage).flatMap((b) => b.questions))) {
            const other = stageOf.get(q);
            if (other && other !== s.stage)
                add(item, `stages.${i}.stage`, 'duplicate_key', `${q} is asked in both ${other} and ${s.stage}`);
            else
                stageOf.set(q, s.stage);
        }
    });
    const where = (ref, stage, strict) => {
        const at = stageOf.get(ref);
        if (!at)
            return `${ref}, which no stage of this journey asks`;
        if (at === stage)
            return strict ? `${ref}, asked in ${stage} itself` : null;
        return ancestors(stage).has(at) ? null : `${ref}, asked in ${at}, which does not come before ${stage}`;
    };
    j.stages.forEach((s, i) => {
        for (const b of bodies(s.stage)) {
            for (const q of b.questions)
                for (const v of questions.get(q) ?? []) {
                    const def = v.body;
                    for (const ref of def.depends_on) {
                        const bad = where(ref, s.stage, false);
                        if (bad)
                            add(item, `stages.${i}`, 'stage_order', `${q} in ${s.stage} waits for ${bad}`);
                    }
                    // Shown or hidden by a later answer would reopen a stage the person has finished.
                    for (const ref of questionRefs(def.visibility)) {
                        if (def.depends_on.includes(ref))
                            continue;
                        const bad = where(ref, s.stage, false);
                        if (bad)
                            add(item, `stages.${i}`, 'stage_order', `${q} in ${s.stage} is shown depending on ${bad}`);
                    }
                }
            questionRefs(b.entry).forEach((ref) => {
                const bad = where(ref, s.stage, true);
                if (bad)
                    add(item, `stages.${i}`, 'stage_order', `${s.stage}'s entry condition reads ${bad}`);
            });
            questionRefs(b.completion).forEach((ref) => {
                const bad = where(ref, s.stage, false);
                if (bad)
                    add(item, `stages.${i}`, 'stage_order', `${s.stage}'s completion condition reads ${bad}`);
            });
            // Routes (task 85) read what comes before and skip only what waits for their stage.
            const waits = (stage) => stage !== s.stage && ancestors(stage).has(s.stage);
            for (const r of b.routes) {
                const route = `${s.stage}'s route after ${r.after}`;
                questionRefs(r.when).forEach((ref) => {
                    const bad = where(ref, s.stage, false);
                    if (bad)
                        add(item, `stages.${i}`, 'route_order', `${route} reads ${bad}`);
                });
                if (r.then.action === 'skip_questions')
                    for (const q of r.then.questions) {
                        const at = stageOf.get(q);
                        if (at && at !== s.stage && !waits(at))
                            add(item, `stages.${i}`, 'route_order', `${route} skips ${q} in ${at}, which doesn’t wait for ${s.stage}`); // prettier-ignore
                    }
                if (r.then.action === 'skip_stages')
                    for (const k of r.then.stages)
                        if (waitsFor.has(k) && !waits(k))
                            add(item, `stages.${i}`, 'route_order', `${route} skips ${k}, which doesn’t wait for ${s.stage}`);
            }
        }
    });
}
/** The questions of its own stage a route skips (goto, skip_questions in the stage, end_stage). */
export function stageLocalTargets(st, r) {
    const at = st.questions.indexOf(r.after);
    if (at < 0)
        return [];
    const later = st.questions.slice(at + 1);
    switch (r.then.action) {
        case 'goto': {
            const to = later.indexOf(r.then.question);
            return to < 0 ? [] : later.slice(0, to);
        }
        case 'skip_questions':
            return r.then.questions.filter((q) => later.includes(q));
        case 'end_stage':
        case 'end_journey':
            return later;
        case 'skip_stages':
            return [];
    }
}
/** A stage's routes on their own: they follow its questions, only go forward, and can all be taken. */
function checkStageRoutes(item, st, questions, stages, add) {
    const always = new Set();
    st.routes.forEach((r, i) => {
        const path = `routes.${i}`;
        const at = st.questions.indexOf(r.after);
        if (at < 0)
            return add(item, `${path}.after`, 'unknown_reference', `route follows ${r.after}, which this stage doesn’t ask`); // prettier-ignore
        if (always.has(r.after))
            add(item, path, 'unreachable', `an earlier route after ${r.after} is always taken, so this one never is`);
        if (!r.when)
            always.add(r.after);
        questionRefs(r.when).forEach((ref) => {
            if (!questions.has(ref))
                add(item, `${path}.when`, 'unknown_reference', `route reads unknown question ${ref}`);
            else if (st.questions.indexOf(ref) > at)
                add(item, `${path}.when`, 'route_order', `route after ${r.after} reads ${ref}, which comes later in the stage`);
        });
        checkOptionValues(item, `${path}.when`, r.when, questions, add);
        const later = new Set(st.questions.slice(at + 1));
        const t = r.then;
        if (t.action === 'goto') {
            if (!st.questions.includes(t.question))
                add(item, `${path}.then`, 'unknown_reference', `route goes to ${t.question}, which this stage doesn’t ask`);
            else if (!later.has(t.question))
                add(item, `${path}.then`, 'route_order', `routes only go forward: ${t.question} doesn’t come after ${r.after}`);
            else if (st.questions.indexOf(t.question) === at + 1)
                add(item, `${path}.then`, 'unreachable', `${t.question} comes straight after ${r.after}, so nothing is skipped`);
        }
        if (t.action === 'skip_questions')
            t.questions.forEach((q, k) => {
                if (!questions.has(q))
                    add(item, `${path}.then.questions.${k}`, 'unknown_reference', `route skips unknown question ${q}`); // prettier-ignore
                else if (q === r.after || (st.questions.includes(q) && !later.has(q)))
                    add(item, `${path}.then.questions.${k}`, 'route_order', `routes only go forward: ${q} doesn’t come after ${r.after}`); // prettier-ignore
                else if (r.skipped_as === 'not_in_place' &&
                    !questions.get(q).some(({ body }) => notInPlaceOption(body)))
                    // prettier-ignore
                    add(item, `${path}.then.questions.${k}`, 'type_mismatch', `${q} has no answer that means “not in place”`);
            });
        if (t.action === 'skip_stages')
            t.stages.forEach((k, n) => {
                if (!stages.has(k))
                    add(item, `${path}.then.stages.${n}`, 'unknown_reference', `route skips unknown stage ${k}`);
            });
        if (t.action === 'end_stage' && at === st.questions.length - 1)
            add(item, `${path}.then`, 'unreachable', `${r.after} is the stage’s last question, so nothing is skipped`);
    });
}
/**
 * The answer a skipped question counts as when a route says "not in place": the option whose effects
 * score its control as not in place, else one called no / not_in_place / never / none.
 */
export function notInPlaceOption(q) {
    const scored = q.options.find((o) => o.effects.some((e) => e.type === 'score_change' && e.params.implementation === 0));
    if (scored)
        return scored.value;
    return ['no', 'not_in_place', 'never', 'none'].find((v) => q.options.some((o) => o.value === v));
}
function checkOptionValues(item, path, c, questions, add) {
    checkTestValues(item, path, questionTests(c), questions, add);
}
function checkTestValues(item, path, tests, questions, add) {
    for (const t of tests) {
        const targets = (questions.get(t.question) ?? []).map(({ body }) => body);
        if (!targets.some((q) => q.options.length))
            continue;
        if (ORDERED_RULE_OPERATORS.has(t.op)) {
            add(item, path, 'type_mismatch', `${t.question} is a choice question; ${t.op} needs a number or date`);
            continue;
        }
        if (t.value === undefined)
            continue;
        const allowed = new Set(targets.flatMap((q) => q.options.map((o) => o.value)));
        for (const v of Array.isArray(t.value) ? t.value : [t.value]) {
            if (!allowed.has(String(v)))
                add(item, path, 'unknown_option', `${t.question} has no option "${v}"`);
        }
    }
}
/** Cycles in a dependency graph, each reported once as the list of keys in the loop. */
function findCycles(nodes, keys, edges) {
    const byKey = new Map(keys.map((k, i) => [k, nodes[i]]));
    const color = new Map();
    const stack = [];
    const cycles = [];
    const visit = (k) => {
        color.set(k, 1);
        stack.push(k);
        for (const next of edges(byKey.get(k))) {
            if (!byKey.has(next))
                continue;
            const c = color.get(next) ?? 0;
            if (c === 1)
                cycles.push(stack.slice(stack.indexOf(next)));
            else if (c === 0)
                visit(next);
        }
        stack.pop();
        color.set(k, 2);
    };
    for (const k of keys)
        if (!color.get(k))
            visit(k);
    return cycles;
}
//# sourceMappingURL=config.js.map